import { useEffect } from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import type { RouteRecord } from 'vite-react-ssg';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import { AuthProvider } from './context/AuthContext';
import { GlobalSEO } from '@/utils/seoManager';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function RootShell() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ScrollToTop />
          <GlobalSEO />
          <Layout />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

// ponytail: 301-style redirect for the duplicate /gig/:id route → /gigs/:id
function GigDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/gigs/${id}`} replace />;
}

// ponytail: adapter — our pages use `export default`, React Router's `lazy`
// expects `{ Component }`. One-liner beats renaming ~40 page exports.
const lazyPage = (loader: () => Promise<{ default: React.ComponentType<any> }>) =>
  () => loader().then(m => ({ Component: m.default }));

export const routes: RouteRecord[] = [
  {
    path: '/',
    element: <RootShell />,
    entry: 'src/components/Layout.tsx',
    errorElement: <ErrorBoundary />,
    children: [
      { index: true, lazy: lazyPage(() => import('./pages/LandingPage')) },
      { path: 'complete-profile', lazy: lazyPage(() => import('./components/auth/ProfileCompletion')) },

      { path: 'gigs', lazy: lazyPage(() => import('./pages/Gigs')) },
      { path: 'how-it-works', lazy: lazyPage(() => import('./pages/HowItWorksPage')) },
      { path: 'gig/:id', element: <GigDetailRedirect /> },
      { path: 'gigs/:id', lazy: lazyPage(() => import('./pages/GigDetail')) },

      { path: 'signin', lazy: lazyPage(() => import('./pages/SignIn')) },
      { path: 'signup', lazy: lazyPage(() => import('./pages/SignUp')) },
      { path: 'forgot-password', lazy: lazyPage(() => import('./pages/ForgotPassword')) },
      { path: 'reset-password', lazy: lazyPage(() => import('./pages/ResetPassword')) },

      { path: 'checkout/:id', lazy: lazyPage(() => import('./pages/ServiceCheckout')) },
      { path: 'wallet', element: <Navigate to="/dashboard/payments" replace /> },

      { path: 'saved-gigs', element: <PrivateRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/SavedGigs')) },
      ]},
      { path: 'payment', lazy: lazyPage(() => import('./pages/Payment')) },
      { path: 'payment/:id', lazy: lazyPage(() => import('./pages/Payment')) },
      { path: 'payment-success', lazy: lazyPage(() => import('./pages/PaymentSuccess')) },
      { path: 'payment-settings', lazy: lazyPage(() => import('./pages/PaymentSettings')) },

      { path: 'gigs/create', element: <PrivateRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/CreateGig')) },
      ]},
      { path: 'gigs/edit/:id', element: <PrivateRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/EditGig')) },
      ]},
      { path: 'gigs/manage', lazy: lazyPage(() => import('./pages/DynamicGigs')) },

      { path: 'orders', element: <PrivateRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/Orders')) },
      ]},
      { path: 'order/:id', element: <PrivateRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/OrderDetail')) },
      ]},
      { path: 'order-review/:id', element: <PrivateRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/OrderReview')) },
      ]},
      { path: 'revision/:id', element: <PrivateRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/RequestRevision')) },
      ]},
      { path: 'dispute/:id', element: <PrivateRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/DisputeSubmission')) },
      ]},

      { path: 'dashboard', element: <PrivateRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/dashboard/DashboardHome')) },
        { path: 'orders', lazy: lazyPage(() => import('./pages/Orders')) },
        { path: 'payments', lazy: lazyPage(() => import('./pages/DashboardPayments')) },
        { path: 'profile', lazy: lazyPage(() => import('./pages/Profile')) },
        { path: 'settings', lazy: lazyPage(() => import('./pages/DashboardSettings')) },
      ]},

      { path: 'messages', element: <PrivateRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/RealtimeChat')) },
      ]},
      { path: 'reviews', element: <PrivateRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/Reviews')) },
      ]},
      { path: 'profile', element: <PrivateRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/Profile')) },
      ]},

      { path: 'u/:username', lazy: lazyPage(() => import('./pages/PublicProfile')) },
      { path: 'profile/:id', lazy: lazyPage(() => import('./pages/PublicProfile')) },

      { path: 'notifications', element: <PrivateRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/NotificationCenter')) },
      ]},
      { path: 'analytics', element: <PrivateRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/Analytics')) },
      ]},

      { path: 'analytics-dashboard', element: <AdminRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/AnalyticsDashboard')) },
      ]},
      { path: 'analytics-dashboard-comprehensive', element: <AdminRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/AnalyticsDashboardComprehensive')) },
      ]},
      { path: 'debug-permissions', element: <AdminRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/DebugPermissions')) },
      ]},
      { path: 'debug-storage', element: <AdminRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/DebugStorage')) },
      ]},
      { path: 'debug-email', element: <AdminRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/DebugEmail')) },
      ]},
      { path: 'admin/disputes', element: <AdminRoute />, children: [
        { index: true, lazy: lazyPage(() => import('./pages/AdminDisputes')) },
      ]},

      { path: 'terms', lazy: lazyPage(() => import('./pages/Terms')) },
      { path: 'privacy', lazy: lazyPage(() => import('./pages/Privacy')) },
      { path: 'categories', lazy: lazyPage(() => import('./pages/Categories')) },
      { path: 'categories/:slug', lazy: lazyPage(() => import('./pages/CategoryLanding')) },
      { path: 'categories/:slug/in/:city', lazy: lazyPage(() => import('./pages/LocationLanding')) },
      { path: 'about', lazy: lazyPage(() => import('./pages/About')) },
      { path: 'help', lazy: lazyPage(() => import('./pages/Help')) },
      { path: 'contact', lazy: lazyPage(() => import('./pages/Contact')) },

      { path: '*', lazy: lazyPage(() => import('./pages/NotFound')) },
    ],
  },
];